<script setup lang="ts">
import type { GeoLocation, GeoSearchResultItem, Locale } from '@smg-real-estate/geo-libs';
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  isNavigationFailure,
  NavigationFailureType,
  type RouteLocationRaw,
  useRoute,
  useRouter,
} from 'vue-router';

import AgencyFocus from '@/components/AgencyFocus/AgencyFocus.vue';
import SearchTypeToggle from '@/components/AgencySearchBar/SearchTypeToggle.vue';
import Locations from '@/components/Locations/Locations.vue';
import { ValidationStates } from '@/components/Locations/validationStates';
import { useLocale } from '@/router/routeMeta';
import { useAgencyProfilesStore } from '@/stores/agencyProfiles';
import { useGeoStore } from '@/stores/geo';

const { t } = useI18n();
const geoSearchResultList = ref<GeoSearchResultItem[]>([]);
const agencyProfilesStore = useAgencyProfilesStore();
const geoStore = useGeoStore();
const { selectedListingType } = storeToRefs(agencyProfilesStore);
const route = useRoute();
const router = useRouter();
const locale = useLocale();

const handleSearchTextChange = async (searchText: string) => {
  if (searchText.length < 2) {
    return;
  }

  await geoStore.autocompleteLocations({
    query: searchText,
    language: locale.value,
  });

  const locations = geoStore.getAutocompleteResults;

  if (!locations) {
    return;
  }

  geoSearchResultList.value = locations.filter((item) => item.geoLocation.type != 'ziparea');
};

const applySearch = () => {
  const nextRoute = agencyProfilesStore.nextPageRoute(1, locale.value as unknown as Locale);
  const {
    page: _page,
    loc: _loc,
    includeChildren: _includeChildren,
    listingType: _listingType,
    sortBy: _sortBy,
    ...preservedQuery
  } = route.query;

  router
    .push({
      params: nextRoute.params,
      query: {
        ...preservedQuery,
        ...nextRoute.query,
      },
    } as unknown as RouteLocationRaw)
    .catch((error) => {
      // Ignore duplicated navigation errors
      if (!isNavigationFailure(error, NavigationFailureType.duplicated)) {
        throw Error(error);
      }
    });
};

const handleLocationChange = (locations: GeoLocation[]) => {
  agencyProfilesStore.changeSelectedLocations(locations);
  applySearch();
};

watch(selectedListingType, () => {
  applySearch();
});
</script>

<template>
  <div>
    <div :class="$style.searchBarWrapper">
      <div :class="$style.searchTypeWrapper">
        <SearchTypeToggle />
      </div>
      <div :class="$style.locationsWrapper">
        <Locations
          data-dd-action-name="SearchBar Locations Input"
          :t="t"
          :locale="locale"
          :searchResults="geoSearchResultList"
          :selectedLocations="agencyProfilesStore.selectedLocations"
          :validationState="ValidationStates.Valid"
          @locationsChanged="handleLocationChange"
          @searchTextUpdated="handleSearchTextChange"
        />
      </div>
      <div :class="$style.focusFilterWrapper">
        <AgencyFocus />
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

.searchBarWrapper {
  display: grid;
  grid-template-columns: 1fr;
  row-gap: $space-100;
  padding-block: $space-100;
  margin-top: $space-150;
  background-color: $color-white;
  border-radius: $border-radius-l;

  @media (min-width: $viewport-sm-min) {
    grid-template-columns: auto 1fr;
    column-gap: $space-150;
  }
}

.searchTypeWrapper {
  grid-row-start: 1;
}

.locationsWrapper {
  grid-row-start: 2;

  @media (min-width: $viewport-sm-min) {
    grid-row-start: 1;
    grid-column-start: 2;
  }
}

.focusFilterWrapper {
  grid-row-start: 3;
  padding-top: $space-50;

  @media (min-width: $viewport-sm-min) {
    grid-row-start: 2;
    grid-column: 1 / -1;
  }
}
</style>
